"use client";

import { Check } from "lucide-react";
import { lessonKey, useLearnProgress } from "./progress";

/* How far this browser has got through one course. Renders nothing until a
   lesson is ticked, so a first-time visitor sees a clean card. */
export default function CourseProgress({
  track,
  lessons,
  className = "",
}: {
  track: string;
  lessons: string[];
  className?: string;
}) {
  const { done } = useLearnProgress();
  const n = lessons.filter((s) => done.has(lessonKey(track, s))).length;
  if (n === 0) return null;

  const pct = Math.round((n / lessons.length) * 100);
  const complete = n === lessons.length;

  if (complete) {
    return (
      <div
        className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${className}`}
        style={{
          background: "color-mix(in srgb, #ffe500 14%, transparent)",
          borderColor: "color-mix(in srgb, #ffe500 70%, transparent)",
        }}
        role="status"
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ffe500] text-black"
          aria-hidden
        >
          <Check className="h-4 w-4" strokeWidth={3} />
        </span>
        <span className="text-[13px] leading-tight">
          <span className="block font-semibold">Course complete</span>
          <span className="block text-muted-foreground">
            All {lessons.length} lessons done
          </span>
        </span>
        <span className="ml-auto text-[12px] font-semibold tabular-nums">100%</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-3 text-[12px]">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          {n} of {lessons.length} lessons done
        </span>
        <span className="text-muted-foreground">{pct}%</span>
      </div>
      <ProgressBar value={pct} label={`${n} of ${lessons.length} lessons done`} />
    </div>
  );
}

export function ProgressBar({ value, label }: { value: number; label: string }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="h-1.5 w-full overflow-hidden rounded-full"
      style={{ background: "var(--hairline-strong)" }}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${value}%`, background: "#ffe500" }}
      />
    </div>
  );
}
